import { IModel, IViolation, Collection } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';

import { ExpressionBasedColumn } from './expression-based.column';
import { ExpressionBasedColumnPattern } from './expression-based.column.pattern';
import { DataFlowColumn } from '../dataset/dataflowcolumn';
import { ColumnExpressionProcessor } from './column-expression-processor';
import { format } from '../../shared/utils/string.util';
import { IValidationContext, UniqueValidator } from 'shell-models-lib';
import { BaseGroupByTransformation } from './base-group-by-transformation';
import { ExpressionBasedColumnBase } from './expression-based.column.base';
import { BaseSchemaModifierTransformation } from './base-schema-modifier-transformation';

@Metamodel.model({
    name: 'aggregate',
    display: [stringResources.AggregateTransformationLabel, stringResources.AggregateTransformationPluralLabel],
    referable: true,
    constraints: {
        name: {
            required: true
        },
        outputColumns: {
            validator: BaseSchemaModifierTransformation.validateOutputColumns
        },
        transientColumns: {
            validator: BaseSchemaModifierTransformation.validateTransientColumns
        }
    }
})
export class AggregateTransformation extends BaseGroupByTransformation {
    public static readonly MODEL: IModel;

    @Metamodel.attribute({
        display: stringResources.AggregateTransformationGroupByColumn,
        type: ExpressionBasedColumn.MODEL,
        collection: Collection.Array,
        constraint: {
            unique: true,
            minOccurs: 0
        }
    })
    public groupByColumns: ExpressionBasedColumn[] = [];

    @Metamodel.attribute({
        display: stringResources.AggregateTransformationAggregateColumn,
        constraint: {
            minOccurs: 1
        }
    })
    public expressionColumns: ExpressionBasedColumnBase[] = [];

    public constructor() {
        super();
        this.inspectSummaryOutputFields.splice(1, 0, {
            name: () => stringResources.TransformationInspect_Dropped,
            value: () => this.droppedColumns.length
        });
        this.inspectDetailsOutputFields.unshift({
            name: () => this.columnInfoTitle,
            value: this.getColumnModificationType.bind(this)
        });
    }

    public static validate(transformation: AggregateTransformation, context?: IValidationContext): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (transformation.outputColumns) {
            const uniqueValidatorViolations = UniqueValidator.INSTANCE.validate({}, AggregateTransformation.MODEL.attribute('outputcolumns'),
                transformation.outputColumns(), transformation, context);
            if (uniqueValidatorViolations) {
                violations.push(...uniqueValidatorViolations);
            }
        }
        transformation.expressionColumns.forEach((aggColumn) => {
            if (aggColumn instanceof ExpressionBasedColumnPattern) {
                if (aggColumn.columns.length === 0) {
                    violations.push({
                        context: aggColumn,
                        message: () => stringResources.RuleBasedColumnNoColumnError
                    });
                }
            }
        });

        return violations;
    }

    public get columns(): DataFlowColumn[] {
        const output = ColumnExpressionProcessor.processNewColumns([].concat(this.groupByColumns, this.expressionColumns),
            this.distinctInputColumnRefs, this).map((processedColumnResult) => processedColumnResult.column);

        this.droppedColumns = [];
        this.unchangedColumns = [];
        this.newColumns = [];
        this.distinctInputColumnRefs.forEach((col) => {
            if (this.groupByColumns.find((v) => v.dataFlowExpression.expression === col.name && v.column.name === col.name)) {
                this.unchangedColumns.push(col.column);
            } else {
                this.droppedColumns.push(col.column);
            }
        });
        this.newColumns = output.filter((v) => this.unchangedColumns.find((col) => col.name === v.name) == null);
        return output;
    }

    public getColumnModificationType(column: DataFlowColumn): string {
        if (column.columnPattern != null || this.expressionColumns.find((v) => (v as ExpressionBasedColumn).column === column) != null) {
            return stringResources.AggregateTransformationLabel;
        }
        if (this.groupByColumns.find((c) => c.column === column) != null) {
            return stringResources.GroupbyLabel;
        }
        return '-';
    }

    protected getGeneratedDescription(): string {
        if (this.expressionColumns.length > 0) {
            const allOutputColumns = this.columns;
            const groupByColumnsLength = this.groupByColumns.length;
            const groupByColumnNames: string[] = [];
            const aggregateColumnNames: string[] = [];
            allOutputColumns.forEach((dataFlowColumn: DataFlowColumn, index: number) => {
                if (dataFlowColumn.name) {
                    // First we put group by columns.
                    if (index < groupByColumnsLength) {
                        groupByColumnNames.push(dataFlowColumn.name);
                    } else {

                        aggregateColumnNames.push(dataFlowColumn.name);
                    }
                }
            });
            return groupByColumnNames.length
                ? format(stringResources.AggregateDescription, groupByColumnNames.join(', '), aggregateColumnNames.join(', '))
                : format(stringResources.AggregateNoGroupDescription, aggregateColumnNames.join(', '));
        }
        return stringResources.AggregateNoColumnsDescription;
    }

    protected getExpressionColumnsDisplayName(): string {
        return stringResources.AggregateTransformationAggregateColumn;
    }
}
